import { ReactNode } from 'react';
import { Tag } from 'antd';
import {
  IconBuilding,
  IconCalendar,
  IconCoinPound,
  IconFileText,
  IconPackage,
  IconReplace,
  IconRoute,
  IconRulerMeasure,
  IconTrendingUp,
  IconUsers,
} from '@tabler/icons-react';

import { FieldType } from '@pmp/api';
import { CrudMode, FormItem, SelectsOptions } from '@ohpen/enso-ui';
import { GetFormSchema } from '@pmp/types';

import { ProductBoSyncStatus } from './components/ProductBoSyncStatus';

type SelectOptions = SelectsOptions[keyof SelectsOptions];

const inTwoColumns = (columns: FormItem[]): FormItem[] => columns.map(column => ({ ...column, colProps: { span: 12 } }));

const section = (title: string, icon: ReactNode, columns: FormItem[]): FormItem => ({
  title,
  icon,
  valueType: 'group',
  variant: 'card',
  collapsible: true,
  columns: inTwoColumns(columns),
});

const asTag = ({ label }: { label?: ReactNode }): ReactNode => <Tag variant="outlined">{label}</Tag>;

// The built-in 'tag' readonly display drops any falsy value, which would turn a Yes/No field
// answered No into a dash.
const renderValueTag = (_dom: ReactNode, entity: { value: unknown; options?: { value: unknown; label: ReactNode }[] }): ReactNode => {
  const { value, options } = entity;

  if (value === null || value === undefined || value === '') return '-';

  const option = options?.find(candidate => candidate.value === value);
  if (option) return <Tag variant="outlined">{option.label}</Tag>;

  return <Tag variant="outlined">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</Tag>;
};

const text = (title: string, dataIndex: string): FormItem => ({ title, dataIndex, valueType: 'text' });

const digit = (title: string, dataIndex: string, disabled = false): FormItem => ({
  title,
  dataIndex,
  valueType: 'digit',
  fieldProps: { disabled },
});

const date = (title: string, dataIndex: string): FormItem => ({ title, dataIndex, valueType: 'date' });

const select = (title: string, dataIndex: string, options: SelectOptions): FormItem => ({
  title,
  dataIndex,
  valueType: 'select',
  fieldProps: { options, optionFilterProp: 'label', allowClear: true, labelRender: asTag },
  renderReadonly: renderValueTag,
});

const multiSelect = (title: string, dataIndex: string, options: SelectOptions): FormItem => ({
  title,
  dataIndex,
  valueType: 'select',
  fieldProps: { options, optionFilterProp: 'label', allowClear: true, mode: 'multiple' },
  readonlyDisplay: 'tag',
});

const required = (item: FormItem): FormItem => ({ ...item, formItemProps: { required: true } });

const boSyncStatus = (productCode: string): FormItem => ({
  title: 'pmp.products.labels.boSyncStatus',
  dataIndex: 'boSyncStatus',
  valueType: 'text',
  renderReadonly: () => <ProductBoSyncStatus productCode={productCode} />,
});

type FormSchemaOptions = {
  excludedFields?: readonly string[];
  boSyncProductCode?: string;
};

export const getFormSchema = (
  selectsOptions: SelectsOptions,
  { excludedFields = [], boSyncProductCode }: FormSchemaOptions = {},
): FormItem[] =>
  buildFormSchema(selectsOptions, boSyncProductCode)
    .map(group => ({
      ...group,
      columns: (group.columns as FormItem[]).filter(column => !excludedFields.includes(String(column.dataIndex))),
    }))
    .filter(group => group.columns.length);

const buildFormSchema = (selectsOptions: SelectsOptions, boSyncProductCode?: string): FormItem[] => [
  section('global.labels.general', <IconFileText />, [
    required(text('pmp.products.labels.code', 'code')),
    required(text('pmp.products.labels.name', 'name')),
    text('pmp.products.labels.group', 'group'),
    ...(boSyncProductCode ? [boSyncStatus(boSyncProductCode)] : []),
  ]),
  section('pmp.products.labels.productDates', <IconCalendar />, [
    required(date('pmp.products.labels.startDate', 'startDate')),
    date('pmp.products.labels.endDate', 'endDate'),
    date('pmp.products.labels.gracePeriodEndDate', 'gracePeriodEndDate'),
    date('pmp.products.labels.productCutoffDate', 'productCutoffDate'),
  ]),
  section('pmp.products.labels.rate', <IconTrendingUp />, [
    required(select('pmp.products.labels.initialRateType', 'initialRateType', selectsOptions[FieldType.INITIAL_RATE_TYPE])),
    select('pmp.products.labels.productTerm', 'productTerm', selectsOptions[FieldType.PRODUCT_TERM]),
    digit('pmp.products.labels.initialPeriodInMonths', 'initialPeriodInMonths'),
    date('pmp.products.labels.initialPeriodUntil', 'initialPeriodUntil'),
    select('pmp.products.labels.baseRateName', 'baseRate', selectsOptions[FieldType.BASE_RATES]),
    digit('pmp.products.labels.baseRate', 'baseRateAmount', true),
    digit('pmp.products.labels.surcharge', 'surcharge'),
    digit('pmp.products.labels.rate', 'rate', true),
    digit('pmp.products.labels.stressRate', 'stressRate'),
  ]),
  section('pmp.products.labels.loanCriteria', <IconRulerMeasure />, [
    digit('pmp.products.labels.minLtv', 'minLtv'),
    digit('pmp.products.labels.maxLtv', 'maxLtv'),
    digit('pmp.products.labels.minTotalLoanAmount', 'minTotalLoanAmount'),
    digit('pmp.products.labels.maxTotalLoanAmount', 'maxTotalLoanAmount'),
    digit('pmp.products.labels.minDurationInMonths', 'minDurationInMonths'),
    digit('pmp.products.labels.maxDurationInMonths', 'maxDurationInMonths'),
    digit('pmp.products.labels.maxLti', 'maxLti'),
  ]),
  section('pmp.products.labels.applicantCriteria', <IconUsers />, [
    required(multiSelect('pmp.products.labels.repaymentTypes', 'repaymentTypes', selectsOptions[FieldType.REPAYMENT_TYPE])),
    multiSelect('pmp.products.labels.regulatoryTypes', 'regulatoryTypes', selectsOptions[FieldType.REGULATORY_TYPE]),
    multiSelect('pmp.products.labels.applicationTypes', 'applicationTypes', selectsOptions[FieldType.APPLICATION_REQUEST_TYPE]),
    multiSelect('pmp.products.labels.applicantTypes', 'applicantTypes', selectsOptions[FieldType.APPLICANT_TYPES]),
    select('pmp.products.labels.employmentType', 'employmentType', selectsOptions[FieldType.EMPLOYMENT_TYPE]),
    select('pmp.products.labels.expat', 'expat', selectsOptions[FieldType.YES_NO]),
  ]),
  section('pmp.products.labels.propertyCriteria', <IconBuilding />, [
    multiSelect('pmp.products.labels.propertyPurposes', 'propertyPurposes', selectsOptions[FieldType.PROPERTY_PURPOSE]),
    multiSelect('pmp.products.labels.propertyTypes', 'propertyTypes', selectsOptions[FieldType.PROPERTY_TYPE]),
    multiSelect('pmp.products.labels.buildingTypes', 'buildingTypes', selectsOptions[FieldType.BUILDING_TYPE]),
    multiSelect('pmp.products.labels.projectTypes', 'projectTypes', selectsOptions[FieldType.PROJECT_TYPE]),
    multiSelect('pmp.products.labels.occupancyTypes', 'occupancyTypes', selectsOptions[FieldType.OCCUPANCY_TYPE]),
  ]),
  section('pmp.products.labels.feesAndErc', <IconCoinPound />, [
    select('pmp.products.labels.feeProfile', 'feeProfile', selectsOptions[FieldType.FEE_PROFILES]),
    select('pmp.products.labels.ercProfile', 'ercProfile', selectsOptions[FieldType.ERC_PROFILES]),
    digit('pmp.products.labels.applicationFeeOverride', 'applicationFeeOverride'),
    digit('pmp.products.labels.productFeePoundsOverride', 'productFeePoundsOverride'),
    digit('pmp.products.labels.productFeePercentOverride', 'productFeePercentOverride'),
    select('pmp.products.labels.productFeeBasedOnOverride', 'productFeeBasedOnOverride', selectsOptions[FieldType.PRODUCT_FEE_BASED_ON]),
  ]),
  section('pmp.products.labels.productFeatures', <IconPackage />, [
    digit('pmp.products.labels.cashback', 'cashback'),
    digit('pmp.products.labels.overpaymentsAllowed', 'overpaymentsAllowed'),
    select('pmp.products.labels.portable', 'portable', selectsOptions[FieldType.YES_NO]),
    select('pmp.products.labels.paymentsHoliday', 'paymentsHoliday', selectsOptions[FieldType.YES_NO]),
    select('pmp.products.labels.feesAssisted', 'feesAssisted', selectsOptions[FieldType.YES_NO]),
    select('pmp.products.labels.green', 'green', selectsOptions[FieldType.YES_NO]),
    select('pmp.products.labels.freeValuation', 'freeValuation', selectsOptions[FieldType.YES_NO]),
  ]),
  section('pmp.products.labels.schemesAndExclusives', <IconRoute />, [
    multiSelect('pmp.products.labels.brands', 'brands', selectsOptions[FieldType.BRANDS]),
    text('pmp.products.labels.region', 'region'),
    multiSelect('pmp.products.labels.schemes', 'schemes', selectsOptions[FieldType.SCHEME]),
    multiSelect('pmp.products.labels.belongsToExclusivitySets', 'belongsToExclusivitySets', selectsOptions[FieldType.EXCLUSIVITY_SETS]),
  ]),
  section('pmp.products.labels.productReplacement', <IconReplace />, [
    text('pmp.products.labels.replacesCode', 'replacesCode'),
    text('pmp.products.labels.replacedByCode', 'replacedByCode'),
    text('pmp.products.labels.followingProductCode', 'followingProductCode'),
  ]),
];

export const getExportProductsFormSchema: GetFormSchema = (mode: CrudMode | null, selectsOptions: SelectsOptions): FormItem[] => {
  return [
    {
      title: 'global.labels.general',
      valueType: 'group',
      fieldProps: {
        collapsible: 'true',
      },
      columns: [
        {
          title: 'global.labels.id',
          dataIndex: 'id',
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'global.labels.label',
          dataIndex: 'name',
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'global.labels.description',
          dataIndex: 'description',
          valueType: 'textarea',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'global.labels.view',
          dataIndex: 'view',
          tooltip: 'global.labels.viewTooltip',
          valueType: 'select',
          fieldProps: {
            options: selectsOptions[FieldType.YES_NO], // Add new FieldType with products views
            disabled: true,
          },
        },
      ],
    },
    {
      title: 'global.labels.replaceColumnHeaders',
      valueType: 'group',
      fieldProps: {
        collapsible: 'true',
      },
      columns: [
        {
          title: 'pmp.products.labels.code',
          dataIndex: ['template', 'code'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.name',
          dataIndex: ['template', 'name'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.productGroup',
          dataIndex: ['template', 'productGroup'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.initialRateType',
          dataIndex: ['template', 'initialRateType'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.baseRate',
          dataIndex: ['template', 'baseRateId'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.surcharge',
          dataIndex: ['template', 'surcharge'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.stressRate',
          dataIndex: ['template', 'stressRate'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.rate',
          dataIndex: ['template', 'rate'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.startDate',
          dataIndex: ['template', 'startDate'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.endDate',
          dataIndex: ['template', 'endDate'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.followingProductCode',
          dataIndex: ['template', 'followingProductCode'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.feesAssisted',
          dataIndex: ['template', 'feesAssisted'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.feeProfile',
          dataIndex: ['template', 'feeProfileId'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.ercProfile',
          dataIndex: ['template', 'ercProfileId'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.minDurationInMonths',
          dataIndex: ['template', 'minDurationInMonths'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.maxDurationInMonths',
          dataIndex: ['template', 'maxDurationInMonths'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.initialPeriodInMonths',
          dataIndex: ['template', 'initialPeriodInMonths'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
          },
        },
        {
          title: 'pmp.products.labels.belongsToExclusivitySets',
          dataIndex: ['template', 'belongsToExclusivitySetIds'],
          valueType: 'text',
          fieldProps: {
            disabled: true,
            mode: 'multiple',
          },
        },
      ],
    },
  ];
};
